import {Injectable} from '@angular/core';
import {
    StoreCreateCommand,
    StoreListQuery,
    StoreListQueryModel, ShelfModel, CategoryShelfModel,
} from '../model/models';
import { HttpClient } from '@angular/common/http';
import { Observable, of as observableOf } from 'rxjs';
import { environment as env } from '../../../../../environments/environment';
import { HttpService } from 'app/core';
import { map, catchError } from 'rxjs/operators';
import { CommandResult } from 'app/core';
import { HttpParams } from '@angular/common/http';
import {
    TableResponseQuery,
    FilterOutputModel,
} from 'app/core';
import {FiltersService, GeneralService, SettingsProvider} from 'app/core';
import { TableHelperService } from 'app/shared/tables/table-helper.service';

@Injectable()
export class StoresDataService {

    constructor(
        private httpClient: HttpClient,
        private http: HttpService,
        private tableHelperService: TableHelperService,
        public filterService: FiltersService,
        public settingsService: SettingsProvider,
        private generalService: GeneralService
    ) {
    }

    shopsIds: number[];

    private static joinCompData(res) {
        return res.items.map((el) => {
            el.AddressWithNR = el.Street + ' ' + el.StreetNumber;
            return el;
        });
    }

    fetchShelvesOdata(
        top: number = 100,
        skip: number = 0,
        sortBy: string = '',
        filter: string = '',
        selectFilters: FilterOutputModel[]
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlGetShelvesOdata}`;
        let params = new HttpParams();
        params = params.set('$count', 'true');

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        // logic to set filter values
        if (filter || (selectFilters && !!selectFilters.length)) {
            const mainFilterString = this.filterService.setFilters(
                filter,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filter,
                    ShelfModel.tableInfo().columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    ShelfModel.tableInfo().columns
                )
            );

            params = params.set('$filter', mainFilterString);
        }

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = this.generalService.toUpperCamelCaseObj(response.body.items);

                return new TableResponseQuery(
                    items,
                    response.body.count
                );
            }),
        );
    }

    updateCategoryShelves(storeId: number, categoryShelves: Array<CategoryShelfModel>) {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlUpdateCategoryShelves}/${storeId}`;

        return this.http.post(url, { CategoryShelves: categoryShelves });
    }

    fetchShelves(): Observable<Array<ShelfModel>> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlGetShelves}`;

        return this.http.get(url).pipe(
            map((response) => {
                return this.generalService.toUpperCamelCaseObj(response);
    }));
    }

    fetchCategoryShelves(
        top: number = 100,
        skip: number = 0,
        sortBy: string = '',
        filter: string = '',
        selectFilters: FilterOutputModel[],
        storeId: number
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlGetCategoryShelves}/${storeId}`;
        let params = new HttpParams();
        params = params.set('$count', 'true');

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        // logic to set filter values
        if (filter || (selectFilters && !!selectFilters.length)) {
            const mainFilterString = this.filterService.setFilters(
                filter,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filter,
                    CategoryShelfModel.tableInfo().columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    CategoryShelfModel.tableInfo().columns
                )
            );

            params = params.set('$filter', mainFilterString);
        }

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = this.generalService.toUpperCamelCaseObj(response.body.items);

                return new TableResponseQuery(
                    items,
                    response.body.count
                );
            }),
        );
    }

    fetchData(
        top: number = 100,
        skip: number = 0,
        sortBy: string = '',
        filter: string = '',
        selectFilters: FilterOutputModel[]
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlGetList}`;
        let params = new HttpParams();
        params = params.set('$count', 'true');

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        // logic to set filter values
        if (filter || (selectFilters && !!selectFilters.length)) {
            const mainFilterString = this.filterService.setFilters(
                filter,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filter,
                    StoreListQueryModel.tableInfo().columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    StoreListQueryModel.tableInfo().columns
                )
            );

            params = params.set('$filter', mainFilterString);
        }

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = this.generalService.toUpperCamelCaseObj(response.body.items);

                return new TableResponseQuery(
                    items,
                    response.body.count
                );
            }),
        );
    }

    fetchSelectData(
        top: number = 100,
        skip: number = 0,
        sortBy: string = '',
        filter: string = '',
        selectFilters: FilterOutputModel[]
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlGetList}`;
        let params = new HttpParams();
        params = params.set('$count', 'true');

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;
        // logic to set filter values
        // if (filter || (selectFilters && !!selectFilters.length)) {
        //     const mainFilterString = this.filterService.setFilters(
        //         filter,
        //         selectFilters,
        //         this.tableHelperService.buildSearchUrl(
        //             filter,
        //             StoreListQuery.getColumnInfo().columns
        //         ),
        //         this.filterService.setSelectFiltersForList(
        //             selectFilters,
        //             CartListQuery.tableInfo().columns
        //         )
        //     );
        //     params = params.set('$filter', mainFilterString);
        // }

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = this.generalService.toUpperCamelCaseObj(response.body.items);

                return new TableResponseQuery(
                    items,
                    response.body.count
                );
            }),
        );
    }

    getById(id: number): Observable<StoreListQuery> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlGetDescription}/${id}`;

        return this.http.get(url).pipe(
            map((response: StoreListQuery) => {
                response = this.generalService.toUpperCamelCaseObj(response);
                response.AddressWithNR =
                    response.Street + ' ' + response.StreetNumber;
                return response;
            })
        );
    }

    fetchInnerData(
        top: number = 100,
        skip: number = 0,
        sortBy: string = 'Id',
        filter: string = '',
        selectFilters: FilterOutputModel[],
        ids?: Array<number>
    ): Observable<TableResponseQuery> {
        ids = ids || this.shopsIds;
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlGetList}?$filter=Id in (
            ${ids.map((id) => (`${id}`))})`;
        let params = new HttpParams();
        params = params.set('$count', 'true');

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        if (filter || (selectFilters && !!selectFilters.length)) {
            const mainFilterString = this.filterService.setFilters(
                filter,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filter,
                    StoreListQuery.getColumnInfo().columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    StoreListQuery.getColumnInfo().columns
                )
            );
            params = params.set('$filter', mainFilterString);
        }

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = this.generalService.toUpperCamelCaseObj(response.body.items);

                return new TableResponseQuery(
                    items,
                    response.body.count
                );
            }),
        );
    }

    getCompetitorsShops(shopId: number): Observable<StoreListQuery[]> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlGetCompetitorsIDs}/${shopId}/competitors`;

        return this.http.get(url).pipe(
            map((response) => {
                response.items = this.generalService.toUpperCamelCaseObj(response.items);
                return StoresDataService.joinCompData(response);
            }),
            catchError((err) => {
                console.log(err);
                return observableOf([]);
            })
        );
    }

    public assignCompToShop(command): Observable<any> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlAssignCompStore}`;
        return this.http.post(url, command);
    }

    create(command: StoreCreateCommand): Observable<CommandResult> {
        const url = `${this.settingsService.configuration.url}${env.DataService.apiUrlCreate}`;
        return this.http.post(url, command);
    }

    update(command: StoreListQuery): Observable<CommandResult> {
        const url = `${this.settingsService.configuration.url}${env.DataService.apiUrlUpdate}`;
        return this.http.put(url, command);
    }

    save(command): Observable<CommandResult> {
        if (command.storeId) {
            return this.update(command);
        } else {
            return this.create(command);
        }
    }

    deleteShelf(id: number): Observable<CommandResult> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlDeleteShelf}/${id}`;
        return this.http.delete(url);
    }

    getShelf(id: number): Observable<ShelfModel> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlGetShelvesDefinitions}/${id}`;
        return this.http.get(url).pipe(map(x => new ShelfModel(x.id, x.name, x.meters)));
    }

    createShelf(command): Observable<CommandResult> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlCreateShelf}`;
        return this.http.post(url, command);
    }

    updateShelf(command): Observable<CommandResult> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlUpdateShelf}/${command.Id}`;
        return this.http.put(url, command);
    }

    saveShelf(command): Observable<CommandResult> {
        if (command.storeId) {
            return this.update(command);
        } else {
            return this.create(command);
        }
    }

    delete(id: number): Observable<CommandResult> {
        const url = `${this.settingsService.configuration.url}${env.StoresDataService.apiUrlDelete}/${id}`;
        return this.http.delete(url);
    }

    getJSON(url): Observable<any> {
        return this.httpClient.get(url);
    }
}
